import React from 'react';
import { TrendingUp, Users, ShoppingBag, Activity, DollarSign } from 'lucide-react';

interface RetailDashboardProps {
  onNavigate: (view: string) => void;
}

const RetailDashboard: React.FC<RetailDashboardProps> = ({ onNavigate }) => {
  const metrics = [
    { label: 'Current Occupancy', value: '127', change: '+8%', icon: Users, color: 'var(--sca-teal)' },
    { label: 'Avg. Dwell Time', value: '18m', change: '+12%', icon: Activity, color: 'var(--sca-teal)' },
    { label: 'Conversion Rate', value: '24%', change: '+5%', icon: TrendingUp, color: 'var(--sca-teal)' },
    { label: 'Sales per SqFt', value: '$423', change: '+15%', icon: DollarSign, color: 'var(--sca-teal)' }
  ];

  const zones = [
    { name: 'Entry Zone', occupancy: 45, trend: 'up' },
    { name: 'Main Floor', occupancy: 62, trend: 'up' },
    { name: 'Fitting Rooms', occupancy: 8, trend: 'stable' },
    { name: 'Checkout Area', occupancy: 12, trend: 'up' }
  ];

  return (
    <div className="p-6" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Retail Analytics
        </h1>
        <p style={{ color: 'var(--sca-muted)' }}>
          Real-time store performance and customer insights
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metrics.map((metric, idx) => (
          <div key={idx} className="card">
            <div className="flex items-center justify-between mb-3">
              <metric.icon size={24} style={{ color: metric.color }} />
              <span className="text-xs px-2 py-1 rounded-full" style={{
                backgroundColor: 'var(--sca-badge-bg)',
                color: 'var(--sca-teal)'
              }}>
                {metric.change}
              </span>
            </div>
            <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
              {metric.value}
            </div>
            <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
              {metric.label}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
            Zone Occupancy
          </h3>
          <div className="space-y-4">
            {zones.map((zone, idx) => (
              <div key={idx}>
                <div className="flex justify-between items-center mb-2">
                  <span style={{ color: 'var(--sca-ink)' }}>{zone.name}</span>
                  <span className="font-semibold" style={{ color: 'var(--sca-teal)' }}>
                    {zone.occupancy}
                  </span>
                </div>
                <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'var(--sca-border)' }}>
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${(zone.occupancy / 100) * 100}%`,
                      backgroundColor: 'var(--sca-teal)'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
            Quick Actions
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => onNavigate('retail-occupancy')}
              className="p-4 rounded-lg text-left transition-all"
              style={{
                border: '1px solid var(--sca-border)',
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--sca-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Users size={20} className="mb-2" style={{ color: 'var(--sca-teal)' }} />
              <div className="text-sm font-medium" style={{ color: 'var(--sca-ink)' }}>
                Store Occupancy
              </div>
            </button>
            <button
              onClick={() => onNavigate('shopper-journey')}
              className="p-4 rounded-lg text-left transition-all"
              style={{
                border: '1px solid var(--sca-border)',
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--sca-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Activity size={20} className="mb-2" style={{ color: 'var(--sca-teal)' }} />
              <div className="text-sm font-medium" style={{ color: 'var(--sca-ink)' }}>
                Shopper Journey
              </div>
            </button>
            <button
              onClick={() => onNavigate('product-interaction')}
              className="p-4 rounded-lg text-left transition-all"
              style={{
                border: '1px solid var(--sca-border)',
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--sca-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <ShoppingBag size={20} className="mb-2" style={{ color: 'var(--sca-teal)' }} />
              <div className="text-sm font-medium" style={{ color: 'var(--sca-ink)' }}>
                Product Interaction
              </div>
            </button>
            <button
              onClick={() => onNavigate('queue-management')}
              className="p-4 rounded-lg text-left transition-all"
              style={{
                border: '1px solid var(--sca-border)',
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--sca-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <TrendingUp size={20} className="mb-2" style={{ color: 'var(--sca-teal)' }} />
              <div className="text-sm font-medium" style={{ color: 'var(--sca-ink)' }}>
                Queue Analytics
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailDashboard;
